import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Confirm, Report } from 'notiflix'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Card, Checkbox, Column, DataTable, EmptyState, Field, FieldGrid, Input, KeyValueList, NativeSelect, PillTone, ProgressBar, StatCard, StatGrid, StatusPill, Toolbar } from '../../../ui'
import { UNATTRIBUTED_LABEL, isUnattributed } from '../commsShared/mailboxes'
import { ConversationCounts, OverviewResponse } from '../commsMailboxWorkspaceView/types'
import styles from '../commsShared/admin.module.scss'

type Phase = 'disabled' | 'failed' | 'backfilling' | 'live' | 'paused' | 'not_started'

/** Same block for every mailbox, Graph or SES: what we hold, how far it is read, where it lands, how much is tied to a case. */
interface MailboxStats {
    held: number
    archived: number
    active: number
    last24h: number
    last7d: number
    last30d: number
    firstReceivedAt: string | null
    lastReceivedAt: string | null
    preprocessed: number
    preprocessOutstanding: number
    extracted: number
    extractionPending: number
    extractionFailed: number
    extractionSkipped: number
    extractedPct: number | null
    categorised: number
    linked: number
    candidates: number
    unlinked: number
    notApplicable: number
    linkedPct: number | null
    needsReview: number
    claims: number
    topCategories: { key: string; total: number }[]
}

interface SesHealth {
    mailbox: string
    state: 'live' | 'stale' | 'error' | 'unwired'
    lastReceivedAt: string | null
    lastRecordedAt: string | null
    staleAfterHours: number
    subscriptions: { total: number | null; comms: number | null; legacy: number | null; stale: number | null; pendingConfirmation: number | null; error: string | null }
    webhookPath: string
}

interface Unattributed {
    mailbox: string
    provider: string | null
    stats: MailboxStats | null
}

interface MailboxCard {
    address: string
    displayName: string | null
    enabled: boolean
    provider: string
    sinceDate: string | null
    lastFolderSyncAt: string | null
    lastSyncedAt: string | null
    lastError: string | null
    phase: Phase
    /** Mail arrives by webhook (SES); nothing to sweep. */
    live: boolean
    stats: MailboxStats | null
    folderCount: number
    includedFolders: number
    estimatedTotal: number
    fetched: number
    held: number
    missing: number
    progressPct: number | null
    ingested: number
    duplicates: number
    noise: number
    linked: number
    errors: number
    folders: { backfilling: number; delta: number; pending: number; paused: number; failed: number }
}

interface Folder {
    id: string
    path: string
    included: boolean
    mode: 'pending' | 'backfill' | 'delta' | 'paused' | 'failed'
    estimatedTotal: number
    fetched: number
    stored: number | null
    missing: number | null
    progressPct: number | null
    ingested: number
    duplicates: number
    noise: number
    linked: number
    errors: number
    lastSyncedAt: string | null
    lastMessageReceivedAt: string | null
    lastError: string | null
    consecutiveFailures: number
    leaseOwner: string | null
}

interface Summary {
    estimatedTotal: number
    fetched: number
    held: number
    missing: number
    progressPct: number | null
    itemsPerMinute: number
    etaMinutes: number | null
    backfilling: boolean
    extractionPending: number | null
    unlinked: number | null
    withCandidates: number | null
}

interface SweepStatus {
    running: boolean
    stopping: boolean
    mailbox: string | null
    startedAt: string | null
    finishedAt: string | null
    passes: number
    lastPass: { at: string; fetched: number; ingested: number; deduped: number; linked: number; errors: number; folders: number } | null
    lastError: string | null
    finishedReason: 'complete' | 'stopped' | 'error' | null
}

const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-GB'))
const fmtDate = (raw: string | null | undefined) => (raw ? new Date(raw).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—')
const fmtEta = (mins: number | null) => {
    if (mins == null) return '—'
    if (mins < 60) return `${mins} min`
    const h = Math.floor(mins / 60)
    return `${h}h ${mins % 60}m`
}

const PHASE_LABEL: Record<Phase, { text: string; tone: PillTone }> = {
    backfilling: { text: 'Backfilling', tone: 'warn' },
    live: { text: 'Live (delta)', tone: 'good' },
    paused: { text: 'Paused', tone: 'neutral' },
    failed: { text: 'Failed', tone: 'bad' },
    disabled: { text: 'Disabled', tone: 'neutral' },
    not_started: { text: 'Not started', tone: 'info' },
}

const SES_STATE: Record<SesHealth['state'], { text: string; tone: PillTone; meaning: string }> = {
    live: { text: 'Live (webhook)', tone: 'good', meaning: 'Mail is arriving through the SES webhook and being recorded as it lands.' },
    stale: { text: 'Quiet', tone: 'warn', meaning: 'Nothing has arrived for a while. Lenders reply most working days, so check the SNS subscription and the SES receipt rule.' },
    unwired: { text: 'Not wired up', tone: 'bad', meaning: 'No live subscription points the inbound topic at this backend, so new mail is not being recorded.' },
    error: { text: 'Cannot check', tone: 'neutral', meaning: 'The SNS subscription check failed; mail may still be arriving.' },
}

const MODE_TONE: Record<Folder['mode'], PillTone> = { pending: 'info', backfill: 'warn', delta: 'good', paused: 'neutral', failed: 'bad' }

const progressTone = (phase?: Phase | Folder['mode']) => (phase === 'live' || phase === 'delta' ? 'good' : phase === 'failed' ? 'bad' : 'accent')

const humanCategory = (key: string) => key.replace(/_/g, ' ')
const pctText = (v: number | null | undefined) => (v == null ? '—' : `${v}%`)

/** Display order on this page: SES stream, then the Graph shared inboxes. */
const MAILBOX_DISPLAY_ORDER = [
    'loc-replies@service.sentinellegal.co.uk',
    'loc-acknowledgement@sentinellegal.co.uk',
    'pcp-complaints@sentinellegal.co.uk',
    'contact@sentinellegal.co.uk',
    'support@sentinellegal.co.uk',
]

const mailboxDisplayRank = (m: { address: string; live?: boolean; provider?: string }) => {
    if (m.live || m.provider === 'ses') return 0
    const i = MAILBOX_DISPLAY_ORDER.indexOf(String(m.address || '').toLowerCase())
    return i === -1 ? MAILBOX_DISPLAY_ORDER.length : i
}

const byMailboxDisplayOrder = (a: { address: string; live?: boolean; provider?: string }, b: { address: string; live?: boolean; provider?: string }) =>
    mailboxDisplayRank(a) - mailboxDisplayRank(b) || String(a.address).localeCompare(String(b.address), 'en-GB')

/** One row of the comparison table: a registered mailbox or an unattributed bucket. */
interface CompareRow {
    key: string
    name: string
    address: string
    provider: string | null
    live: boolean
    registered: boolean
    stats: MailboxStats
}

/**
 * The same four figures for every mailbox, whatever the provider, so the SES
 * stream and the Graph mailboxes can be read side by side.
 */
const MailboxStatsBlock = ({ stats, downloaded, conversations, onOpenInbox, onOpenAwaitingUs }: { stats: MailboxStats | null; downloaded?: number; conversations?: ConversationCounts | null; onOpenInbox?: () => void; onOpenAwaitingUs?: () => void }) => {
    if (!stats || stats.held === 0) return <p className={styles.sub}>Nothing downloaded from this mailbox yet.</p>
    const waiting = stats.extractionPending + stats.extractionFailed
    // The sweep can hold more than the list shows: an email sent to two of our mailboxes is stored once and listed under the mailbox that fetched it first.
    const held = Math.max(downloaded ?? 0, stats.held)
    const elsewhere = held - stats.held
    return (
        <div className={styles.stack} style={{ gap: 8 }}>
            <StatGrid>
                <StatCard compact label="Downloaded" value={held} sub={`${fmt(stats.last24h)} in the last 24h · ${fmt(stats.last7d)} this week · ${fmt(stats.archived)} archived${elsewhere > 0 ? ` · ${fmt(elsewhere)} listed under another mailbox` : ''}`} onClick={onOpenInbox} />
                {conversations && (
                    <StatCard
                        compact
                        label="Email threads"
                        value={conversations.conversations}
                        sub={`${fmt(conversations.awaitingUs)} awaiting us · ${fmt(conversations.awaitingLender)} awaiting lender · ${fmt(conversations.awaitingClient)} awaiting client`}
                        tone={conversations.awaitingUs > 0 ? 'warn' : 'default'}
                        onClick={onOpenAwaitingUs}
                    />
                )}
                <StatCard
                    compact
                    label="Read and categorised"
                    value={pctText(stats.extractedPct)}
                    format={false}
                    sub={waiting > 0 ? `${fmt(stats.extracted)} read · ${fmt(stats.extractionPending)} waiting${stats.extractionFailed ? ` · ${fmt(stats.extractionFailed)} failed` : ''}` : `${fmt(stats.extracted)} read · nothing waiting`}
                    tone={stats.extractionFailed > 0 ? 'warn' : stats.extractedPct === 100 ? 'good' : 'default'}
                />
                <StatCard
                    compact
                    label="Linked to a case"
                    value={pctText(stats.linkedPct)}
                    format={false}
                    sub={`${fmt(stats.linked)} linked · ${fmt(stats.candidates)} possible matches · ${fmt(stats.unlinked)} not linked · ${fmt(stats.claims)} claims`}
                    tone={stats.linkedPct != null && stats.linkedPct >= 90 ? 'good' : stats.unlinked > stats.linked ? 'warn' : 'default'}
                />
                <StatCard compact label="Needs a person" value={stats.needsReview} sub="Possible matches, unlinked after reading, or facts that differ from our record" tone={stats.needsReview > 0 ? 'warn' : 'good'} />
            </StatGrid>
            <div className={styles.counts}>
                <span>Top categories:</span>
                {stats.topCategories.length === 0 ? (
                    <span>none yet — categories appear once emails have been read</span>
                ) : (
                    stats.topCategories.map((c) => (
                        <StatusPill key={c.key} tone="neutral" size="sm" title={`${fmt(c.total)} email(s)`}>
                            {humanCategory(c.key)} · {fmt(c.total)}
                        </StatusPill>
                    ))
                )}
                {stats.categorised > 0 && <span className={styles.sub}>{fmt(stats.categorised)} categorised in total</span>}
            </div>
        </div>
    )
}

export const CommsMailboxesView = () => {
    const navigate = useNavigate()
    const [mailboxes, setMailboxes] = useState<MailboxCard[]>([])
    const [unattributed, setUnattributed] = useState<Unattributed[]>([])
    const [sesHealth, setSesHealth] = useState<SesHealth | null>(null)
    const [conversationsByMailbox, setConversationsByMailbox] = useState<Record<string, ConversationCounts | null>>({})
    const [summary, setSummary] = useState<Summary | null>(null)
    const [sweep, setSweep] = useState<SweepStatus | null>(null)
    /** The production kill switch (Pipeline page → Comms settings); the Sweep buttons are refused while it is on. */
    const [pause, setPause] = useState<{ paused: boolean; by: string | null; at: string | null; source: string } | null>(null)
    const [folders, setFolders] = useState<Record<string, Folder[]>>({})
    const [expanded, setExpanded] = useState<Record<string, boolean>>({})
    const [busy, setBusy] = useState<string | null>(null)
    const [addOpen, setAddOpen] = useState(false)
    const [draft, setDraft] = useState({ address: '', displayName: '', sinceDate: '', defaultPartyType: 'unknown' })

    // One load at a time: the poll below skips a tick if the previous load
    // (two requests, the second an expensive read model) is still in flight.
    const loadInFlight = useRef(false)
    const load = useCallback(async () => {
        if (loadInFlight.current) return
        loadInFlight.current = true
        try {
            const resp = await api({ url: 'comms/mailboxes/list', data: {} })
            if (resp.data?.success) {
                setMailboxes(resp.data.mailboxes || [])
                setUnattributed(resp.data.unattributed || [])
                setSesHealth(resp.data.sesHealth || null)
                setSummary(resp.data.summary || null)
                setSweep(resp.data.sweep || null)
                setPause(resp.data.pause || null)
            }
        } catch (err: any) {
            Report.failure('Load failed', err?.response?.data?.message || 'Could not load mailboxes.', 'Okay')
        }
        // Conversation counts are a separate, slower read model; the page is usable without them.
        try {
            const ov = await api({ url: 'comms/inbox/overview', data: {} })
            if (ov.data?.success) {
                const next: Record<string, ConversationCounts | null> = {}
                for (const m of (ov.data as OverviewResponse).mailboxes) next[m.address.toLowerCase()] = m.conversations
                setConversationsByMailbox(next)
            }
        } catch {
            /* optional */
        } finally {
            loadInFlight.current = false
        }
    }, [])

    const loadFolders = useCallback(async (address: string) => {
        try {
            const resp = await api({ url: 'comms/mailboxes/folders', data: { address } })
            if (resp.data?.success) setFolders((prev) => ({ ...prev, [address]: resp.data.folders || [] }))
        } catch (err: any) {
            Report.failure('Load failed', err?.response?.data?.message || 'Could not load folders.', 'Okay')
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    // Poll while a sweep is running or anything is backfilling so the percentages move.
    useEffect(() => {
        if (!summary?.backfilling && !sweep?.running) return
        const t = setInterval(() => {
            load()
            Object.keys(expanded)
                .filter((k) => expanded[k])
                .forEach(loadFolders)
        }, 10_000)
        return () => clearInterval(t)
    }, [summary?.backfilling, sweep?.running, load, loadFolders, expanded])

    const toggleExpanded = (address: string) => {
        const next = !expanded[address]
        setExpanded((p) => ({ ...p, [address]: next }))
        if (next && !folders[address]) loadFolders(address)
    }

    const post = async (label: string, url: string, data: Record<string, unknown>, after?: () => void) => {
        setBusy(label)
        try {
            const resp = await api({ url, data })
            if (resp.data?.success === false) Report.warning(label, resp.data.message || 'Did not complete.', 'Okay')
            await load()
            after?.()
            return resp.data
        } catch (err: any) {
            Report.failure(`${label} failed`, err?.response?.data?.message || 'Please try again.', 'Okay')
        } finally {
            setBusy(null)
        }
    }

    const handleRun = (address: string | null) =>
        post('Run', 'comms/mailboxes/run', { address }, () => {
            if (address) loadFolders(address)
        })

    const handleStop = () => post('Stop', 'comms/mailboxes/stop', {})

    const sweepLine = (() => {
        if (!sweep) return null
        const scope = sweep.mailbox ? sweep.mailbox : 'all mailboxes'
        if (sweep.running) {
            const lp = sweep.lastPass
            return `${sweep.stopping ? 'Stopping after this pass' : 'Sweep running'} · ${scope} · pass ${sweep.passes + 1} since ${fmtDate(sweep.startedAt)}${lp ? ` · last pass ${fmt(lp.ingested)} new, ${fmt(lp.deduped)} already downloaded, ${fmt(lp.errors)} errors` : ''}`
        }
        if (sweep.finishedReason === 'complete') return `Last sweep finished ${fmtDate(sweep.finishedAt)} · ${scope} · every included folder is on delta polling`
        if (sweep.finishedReason === 'stopped') return `Last sweep stopped ${fmtDate(sweep.finishedAt)} after ${sweep.passes} pass${sweep.passes === 1 ? '' : 'es'} · resumes where it left off`
        if (sweep.finishedReason === 'error') return `Last sweep failed ${fmtDate(sweep.finishedAt)}: ${sweep.lastError}`
        return null
    })()

    const handleResweep = (folder: Folder, address: string) => {
        const since = window.prompt(`Re-sweep "${folder.path}" from date (YYYY-MM-DD), or leave blank for everything:`, '')
        if (since === null) return
        Confirm.show('Re-sweep folder', `Restart the sweep of ${folder.path}${since ? ` from ${since}` : ' from the beginning'}? Already-stored emails are skipped.`, 'Re-sweep', 'Cancel', () =>
            post('Re-sweep', 'comms/mailboxes/resweep', { folderId: folder.id, since: since || null }, () => loadFolders(address))
        )
    }

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!draft.address.trim()) return
        await post('Add mailbox', 'comms/mailboxes/upsert', {
            address: draft.address.trim(),
            displayName: draft.displayName.trim() || null,
            sinceDate: draft.sinceDate || null,
            defaultPartyType: draft.defaultPartyType,
            enabled: true,
        })
        setAddOpen(false)
        setDraft({ address: '', displayName: '', sinceDate: '', defaultPartyType: 'unknown' })
    }

    const openMailbox = (address: string, tab: 'overview' | 'received' = 'overview') => navigate(`/comms/mailboxes/${encodeURIComponent(address)}${tab === 'overview' ? '' : `?tab=${tab}`}`)
    /** SES mail recorded before the SES label mailbox existed (source_mailbox NULL). */
    const unattributedSes = unattributed.filter((u) => isUnattributed(u.mailbox) && u.provider === 'ses').reduce((a, u) => a + (u.stats?.held ?? 0), 0)
    const orderedMailboxes = useMemo(() => [...mailboxes].sort(byMailboxDisplayOrder), [mailboxes])

    const [compareSort, setCompareSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({ key: 'held', direction: 'desc' })

    const compareColumns = useMemo<Column<CompareRow>[]>(
        () => [
            {
                key: 'name',
                header: 'Mailbox',
                sortable: true,
                sortValue: (r) => r.name,
                render: (r) => (
                    <div className={styles.cellStack}>
                        <strong>
                            {r.name}{' '}
                            {r.live && (
                                <StatusPill tone="good" size="sm">
                                    live
                                </StatusPill>
                            )}
                            {!r.registered && (
                                <StatusPill tone="warn" size="sm" title="These emails carry no mailbox label yet. The SES backfill stamps them so they move under the SES mailbox.">
                                    unlabelled
                                </StatusPill>
                            )}
                        </strong>
                        {r.address && <span className={styles.sub}>{r.address}</span>}
                    </div>
                ),
            },
            { key: 'held', header: 'Downloaded', align: 'right', sortable: true, sortValue: (r) => r.stats.held, render: (r) => fmt(r.stats.held) },
            { key: 'last7d', header: 'This week', align: 'right', sortable: true, sortValue: (r) => r.stats.last7d, render: (r) => fmt(r.stats.last7d) },
            {
                key: 'extractedPct',
                header: 'Read',
                align: 'right',
                sortable: true,
                sortValue: (r) => r.stats.extractedPct ?? -1,
                render: (r) => (
                    <span title={`${fmt(r.stats.extracted)} read · ${fmt(r.stats.extractionPending)} waiting · ${fmt(r.stats.extractionFailed)} failed`}>
                        {pctText(r.stats.extractedPct)} <span className={styles.sub}>{fmt(r.stats.extractionPending)} waiting</span>
                    </span>
                ),
            },
            {
                key: 'topCategory',
                header: 'Top category',
                sortable: true,
                sortValue: (r) => r.stats.topCategories[0]?.key ?? '',
                render: (r) => (r.stats.topCategories[0] ? `${humanCategory(r.stats.topCategories[0].key)} (${fmt(r.stats.topCategories[0].total)})` : '—'),
            },
            {
                key: 'linkedPct',
                header: 'Linked',
                align: 'right',
                sortable: true,
                sortValue: (r) => r.stats.linkedPct ?? -1,
                render: (r) => (
                    <span title={`${fmt(r.stats.linked)} linked · ${fmt(r.stats.candidates)} possible matches`}>
                        {pctText(r.stats.linkedPct)} <span className={styles.sub}>{fmt(r.stats.linked)}</span>
                    </span>
                ),
            },
            { key: 'unlinked', header: 'Not linked', align: 'right', sortable: true, sortValue: (r) => r.stats.unlinked, render: (r) => (r.stats.unlinked ? <span className={styles.error}>{fmt(r.stats.unlinked)}</span> : '0') },
            { key: 'claims', header: 'Claims', align: 'right', sortable: true, sortValue: (r) => r.stats.claims, render: (r) => fmt(r.stats.claims) },
            { key: 'needsReview', header: 'Needs a person', align: 'right', sortable: true, sortValue: (r) => r.stats.needsReview, render: (r) => (r.stats.needsReview ? <strong>{fmt(r.stats.needsReview)}</strong> : '0') },
            { key: 'lastReceivedAt', header: 'Last received', sortable: true, sortValue: (r) => r.stats.lastReceivedAt ?? '', render: (r) => <span className={styles.sub}>{fmtDate(r.stats.lastReceivedAt)}</span> },
        ],
        []
    )

    // Sorted here (controlled) so the default order is by volume rather than by address.
    const compareRows = useMemo<CompareRow[]>(() => {
        const rows: CompareRow[] = orderedMailboxes
            .filter((m) => m.stats && m.stats.held > 0)
            .map((m) => ({ key: m.address, name: m.displayName || m.address, address: m.address, provider: m.provider, live: m.live, registered: true, stats: m.stats as MailboxStats }))
        for (const u of unattributed) {
            if (!u.stats || u.stats.held === 0) continue
            rows.push({ key: u.mailbox, name: isUnattributed(u.mailbox) ? UNATTRIBUTED_LABEL : u.mailbox, address: isUnattributed(u.mailbox) ? '' : u.mailbox, provider: u.provider, live: u.provider === 'ses', registered: false, stats: u.stats })
        }
        if (!compareSort) return rows
        const col = compareColumns.find((c) => c.key === compareSort.key)
        const value = col?.sortValue
        if (!value) return rows
        const cmp = (a: CompareRow, b: CompareRow) => {
            const x = value(a)
            const y = value(b)
            if (x == null && y == null) return 0
            if (x == null) return 1
            if (y == null) return -1
            if (typeof x === 'number' && typeof y === 'number') return x - y
            return String(x).localeCompare(String(y), 'en-GB', { numeric: true, sensitivity: 'base' })
        }
        rows.sort(cmp)
        return compareSort.direction === 'desc' ? rows.reverse() : rows
    }, [orderedMailboxes, unattributed, compareSort, compareColumns])

    return (
        <div className={styles.stack}>
            {pause?.paused && (
                <Banner tone="bad" title="Production pipeline stopped">
                    {pause.source === 'env' ? 'COMMS_PIPELINE_PAUSED is set on this API.' : `Stopped${pause.by ? ` by ${pause.by}` : ''}${pause.at ? ` at ${fmtDate(pause.at)}` : ''}.`} No sweeping, reading or matching runs
                    until it is started again from the{' '}
                    <Button size="sm" variant="link" onClick={() => navigate('/comms/pipeline')}>
                        Pipeline page
                    </Button>
                    . Mail still arrives and is kept.
                </Banner>
            )}

            {summary && (
                <StatGrid>
                    <StatCard label="Unique emails downloaded" value={summary.held} sub={`${summary.missing > 0 ? `${fmt(summary.missing)} still to download` : 'nothing left to download'} · Graph mailboxes only`} tone={summary.backfilling || summary.missing > 0 ? 'warn' : 'good'} />
                    <StatCard label="Emails / minute" value={summary.itemsPerMinute} sub="swept in the last 15 min" />
                    <StatCard label={summary.backfilling ? 'ETA for backfill' : 'Polling'} value={summary.backfilling ? fmtEta(summary.etaMinutes) : 'live'} format={false} sub={summary.backfilling ? undefined : 'all folders on delta polling'} />
                    <StatCard label="Awaiting extraction" value={summary.extractionPending ?? '—'} sub="all mailboxes, incl. SES" />
                    <StatCard label="Unlinked" value={summary.unlinked ?? '—'} sub={`${fmt(summary.withCandidates)} with candidates · all mailboxes`} />
                </StatGrid>
            )}

            <Toolbar
                end={
                    <Button size="sm" variant={addOpen ? 'neutral' : 'primary'} onClick={() => setAddOpen((p) => !p)}>
                        {addOpen ? 'Cancel' : 'Add mailbox'}
                    </Button>
                }
            >
                <Button size="sm" onClick={load} disabled={busy !== null}>
                    Refresh
                </Button>
                {sweep?.running ? (
                    <Button size="sm" variant="neutral" onClick={handleStop} disabled={busy !== null || sweep.stopping} loading={busy === 'Stop'}>
                        {sweep.stopping ? 'Stopping…' : 'Stop sweep'}
                    </Button>
                ) : (
                    <Button size="sm" onClick={() => handleRun(null)} disabled={busy !== null} loading={busy === 'Run'}>
                        Sweep all mailboxes
                    </Button>
                )}
                <span className={styles.sub}>{sweepLine ?? 'A sweep runs pass after pass until every included folder is on delta polling; the scheduled job then keeps folders current every 5 minutes. The SES mailbox is never swept: its mail arrives on its own.'}</span>
            </Toolbar>

            {addOpen && (
                <Card as="section" title="Add mailbox" subtitle="The app registration needs Mail.Read on this mailbox (application access policy). Folders are discovered on the next pass.">
                    <form onSubmit={handleAdd}>
                        <FieldGrid>
                            <Field label="Address" required>
                                {(id) => <Input id={id} type="email" required value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} placeholder="mailbox@sentinellegal.co.uk" />}
                            </Field>
                            <Field label="Display name" optional>
                                {(id) => <Input id={id} value={draft.displayName} onChange={(e) => setDraft({ ...draft, displayName: e.target.value })} />}
                            </Field>
                            <Field label="Sweep from" hint="Blank sweeps everything">
                                {(id) => <Input id={id} type="date" value={draft.sinceDate} onChange={(e) => setDraft({ ...draft, sinceDate: e.target.value })} />}
                            </Field>
                            <Field label="Default party">
                                {(id) => <NativeSelect id={id} value={draft.defaultPartyType} onChange={(e) => setDraft({ ...draft, defaultPartyType: e.target.value })} options={['unknown', 'lender', 'client', 'court', 'fos', 'internal'].map((p) => ({ value: p, label: p }))} />}
                            </Field>
                        </FieldGrid>
                        <div className={styles.formFooter}>
                            <Button type="submit" variant="primary" size="sm" disabled={busy !== null}>
                                Add
                            </Button>
                        </div>
                    </form>
                </Card>
            )}

            {orderedMailboxes.map((m) => {
                const health = m.live ? sesHealth : null
                const sesState = health ? SES_STATE[health.state] : null
                const phase = m.live && sesState ? { text: sesState.text, tone: sesState.tone } : m.live ? { text: 'Live (webhook)', tone: 'good' as PillTone } : PHASE_LABEL[m.phase]
                const list = folders[m.address]
                return (
                    <Card
                        key={m.address}
                        as="section"
                        title={
                            <>
                                {m.displayName || m.address}
                                <StatusPill tone={m.enabled ? phase.tone : 'neutral'} dot size="sm">
                                    {m.enabled ? phase.text : 'Disabled'}
                                </StatusPill>
                                {m.folders.failed > 0 && (
                                    <StatusPill tone="bad" size="sm">
                                        {m.folders.failed} folder(s) failed
                                    </StatusPill>
                                )}
                            </>
                        }
                        subtitle={
                            m.live ? (
                                <>
                                    <span className={styles.mono}>{m.address}</span> · label for mail arriving through Amazon SES (replies to the letters we send) · since{' '}
                                    {m.sinceDate ? new Date(m.sinceDate).toLocaleDateString('en-GB') : 'the beginning'} · last received {fmtDate(m.stats?.lastReceivedAt)}
                                </>
                            ) : (
                                <>
                                    <span className={styles.mono}>{m.address}</span> · {m.provider} · since {m.sinceDate ? new Date(m.sinceDate).toLocaleDateString('en-GB') : 'the beginning'} · folders discovered {fmtDate(m.lastFolderSyncAt)} · last sync {fmtDate(m.lastSyncedAt)}
                                </>
                            )
                        }
                        actions={
                            <div className={styles.actions}>
                                <Button size="sm" variant="primary" onClick={() => openMailbox(m.address)}>
                                    Open mailbox
                                </Button>
                                {m.live ? (
                                    <span className={styles.sub} title="Mail arrives on its own through the SES webhook; there are no folders to sweep, pause or re-sync.">
                                        Nothing to sweep
                                    </span>
                                ) : (
                                    <Checkbox label="Enabled" checked={m.enabled} disabled={busy !== null} onChange={(e) => post('Toggle', 'comms/mailboxes/set-enabled', { address: m.address, enabled: e.target.checked })} />
                                )}
                                {m.live ? null : sweep?.running && sweep.mailbox === m.address ? (
                                    <Button size="sm" variant="neutral" disabled={busy !== null || sweep.stopping} onClick={handleStop}>
                                        {sweep.stopping ? 'Stopping…' : 'Stop sweep'}
                                    </Button>
                                ) : (
                                    <Button size="sm" disabled={busy !== null || Boolean(sweep?.running)} onClick={() => handleRun(m.address)}>
                                        Sweep now
                                    </Button>
                                )}
                                {m.live ? null : m.folders.paused + m.folders.failed > 0 ? (
                                    <Button size="sm" disabled={busy !== null} onClick={() => post('Resume', 'comms/mailboxes/resume', { address: m.address }, () => loadFolders(m.address))}>
                                        Resume all
                                    </Button>
                                ) : (
                                    <Button size="sm" disabled={busy !== null} onClick={() => post('Pause', 'comms/mailboxes/pause', { address: m.address }, () => loadFolders(m.address))}>
                                        Pause
                                    </Button>
                                )}
                                {!m.live && (
                                    <Button size="sm" variant="link" onClick={() => toggleExpanded(m.address)}>
                                        {expanded[m.address] ? 'Hide folders' : `Folders (${m.includedFolders}/${m.folderCount})`}
                                    </Button>
                                )}
                            </div>
                        }
                    >
                        {m.live ? (
                            <div className={styles.stack} style={{ gap: 8 }}>
                                <p className={styles.note} style={{ margin: 0 }}>
                                    {sesState?.meaning ?? 'Mail arrives on its own: SES receives the reply, stores it and notifies this backend, which records it straight away.'} Nothing to sweep — the figures below are live.
                                </p>
                                {health && (
                                    <KeyValueList
                                        columns
                                        items={[{ label: 'Last email recorded', value: `${fmtDate(health.lastRecordedAt)}${health.lastReceivedAt ? ` (sent ${fmtDate(health.lastReceivedAt)})` : ''}` }]}
                                    />
                                )}
                            </div>
                        ) : (
                            <>
                                <ProgressBar value={m.progressPct ?? 0} tone={progressTone(m.phase)} showPercentage={false} thin />
                                <div className={styles.counts} style={{ marginTop: 8 }}>
                                    <span>
                                        <strong className={styles.pct}>{m.progressPct == null ? '—' : `${m.progressPct}%`}</strong> <strong>{fmt(m.held)}</strong> unique emails downloaded
                                    </span>
                                    <span>
                                        <strong>{fmt(m.missing)}</strong> still to download
                                    </span>
                                    <span>
                                        <strong>{fmt(m.ingested)}</strong> new
                                    </span>
                                    <span>
                                        <strong>{fmt(m.noise)}</strong> noise
                                    </span>
                                    <span>
                                        <strong>{fmt(m.linked)}</strong> linked to a case
                                    </span>
                                    {m.errors > 0 && (
                                        <span className={styles.error}>
                                            <strong>{fmt(m.errors)}</strong> errors
                                        </span>
                                    )}
                                    <span>
                                        folders: {m.folders.backfilling} backfilling · {m.folders.delta} live · {m.folders.pending} pending
                                    </span>
                                </div>
                            </>
                        )}
                        {m.lastError && (
                            <Banner tone="bad" style={{ marginTop: 12 }}>
                                {m.lastError}
                            </Banner>
                        )}

                        <div style={{ marginTop: 12 }}>
                            <MailboxStatsBlock stats={m.stats} downloaded={m.live ? undefined : m.held} conversations={conversationsByMailbox[m.address.toLowerCase()] ?? null} onOpenInbox={() => openMailbox(m.address, 'received')} onOpenAwaitingUs={() => navigate(`/comms/mailboxes/${encodeURIComponent(m.address)}?tab=received&state=awaiting_us`)} />
                            {m.live && unattributedSes > 0 && (
                                <Banner tone="info" style={{ marginTop: 8 }}>
                                    {fmt(unattributedSes)} email(s) arrived through SES without a mailbox label. They are counted under this card and in its workspace already; the label is only missing on the row itself, and a one-off stamp (a gated update) tidies it away for good.
                                </Banner>
                            )}
                        </div>

                        {!m.live && expanded[m.address] && (
                            <div style={{ marginTop: 12 }}>
                                {!list ? (
                                    <EmptyState bare tone="loading" title="Loading folders…" />
                                ) : list.length === 0 ? (
                                    <EmptyState bare title="No folders discovered yet" description="Run a pass to discover them." />
                                ) : (
                                    <div className={styles.tableWrap}>
                                        <table className={styles.table}>
                                            <thead>
                                                <tr>
                                                    <th>Include</th>
                                                    <th>Folder</th>
                                                    <th>Mode</th>
                                                    <th style={{ width: 220 }}>Progress</th>
                                                    <th>New</th>
                                                    <th>Dup</th>
                                                    <th>Noise</th>
                                                    <th>Linked</th>
                                                    <th>Last synced</th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {list.map((f) => (
                                                    <tr key={f.id}>
                                                        <td>
                                                            <Checkbox label="" aria-label={`Include ${f.path}`} checked={f.included} disabled={busy !== null} onChange={(e) => post('Folder', 'comms/mailboxes/set-folder', { folderId: f.id, included: e.target.checked }, () => loadFolders(m.address))} />
                                                        </td>
                                                        <td>
                                                            {f.path}
                                                            {f.lastError && (
                                                                <div className={styles.error} title={f.lastError}>
                                                                    {f.lastError.slice(0, 120)}
                                                                    {f.consecutiveFailures ? ` (${f.consecutiveFailures}×)` : ''}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {f.leaseOwner && (
                                                                <StatusPill tone="info" size="sm" title={`Being swept by `}>
                                                                    sweeping · {f.leaseOwner.split(':')[0]}
                                                                </StatusPill>
                                                            )}
                                                            <StatusPill tone={MODE_TONE[f.mode]} size="sm">
                                                                {f.mode}
                                                            </StatusPill>
                                                        </td>
                                                        <td>
                                                            <div className={styles.cellStack}>
                                                                <ProgressBar value={f.progressPct ?? 0} tone={progressTone(f.mode)} showPercentage={false} thin />
                                                                <span className={styles.sub}>
                                                                    {f.progressPct == null ? '' : `${f.progressPct}% · `}
                                                                    {fmt(f.stored ?? f.fetched)} / {fmt(f.estimatedTotal)} downloaded
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>{fmt(f.ingested)}</td>
                                                        <td>{fmt(f.duplicates)}</td>
                                                        <td>{fmt(f.noise)}</td>
                                                        <td>{fmt(f.linked)}</td>
                                                        <td className={styles.sub}>{fmtDate(f.lastSyncedAt)}</td>
                                                        <td>
                                                            <div className={styles.rowActions}>
                                                                {f.mode === 'paused' || f.mode === 'failed' ? (
                                                                    <Button size="sm" variant="link" disabled={busy !== null} onClick={() => post('Resume', 'comms/mailboxes/resume', { folderId: f.id }, () => loadFolders(m.address))}>
                                                                        Resume
                                                                    </Button>
                                                                ) : (
                                                                    <Button size="sm" variant="link" disabled={busy !== null} onClick={() => post('Pause', 'comms/mailboxes/pause', { folderId: f.id }, () => loadFolders(m.address))}>
                                                                        Pause
                                                                    </Button>
                                                                )}
                                                                <Button size="sm" variant="link" disabled={busy !== null} onClick={() => handleResweep(f, m.address)}>
                                                                    Re-sweep
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                )
            })}
            {orderedMailboxes.length === 0 && <EmptyState title="No mailboxes configured" description="Apply the comms migrations, then add one above." />}

            {compareRows.length > 0 && (
                <Card
                    as="section"
                    title="Compare mailboxes"
                    subtitle="Every mailbox measured the same way, whether it is swept from Microsoft 365 or arrives live through SES. Click a column to sort; click a row to open that mailbox's workspace."
                    padding="none"
                >
                    <DataTable<CompareRow>
                        columns={compareColumns}
                        rows={compareRows}
                        rowKey={(r) => r.key}
                        bare
                        dense
                        onRowClick={(r) => {
                            if (r.address) openMailbox(r.address)
                        }}
                        rowTone={(r) => (!r.registered ? 'warn' : undefined)}
                        sort={compareSort}
                        onSortChange={setCompareSort}
                    />
                    {unattributed.some((u) => isUnattributed(u.mailbox) && u.stats && u.stats.held > 0) && (
                        <Banner tone="info" style={{ margin: 12 }}>
                            "{UNATTRIBUTED_LABEL}" is mail whose row carries no mailbox label. SES mail is folded into the SES mailbox everywhere it is counted, so anything still shown here came from a sweep that recorded no label; it is in the same reading and linking queues as everything else.
                        </Banner>
                    )}
                </Card>
            )}
        </div>
    )
}
